import { type FC, useCallback, useState } from "react";

import { type ViewColumnDef } from "@moment/api-collab/api-types";

import { Button } from "~/ui/Button";
import { Input } from "~/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "~/ui/Popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/Select";

import { type BaseViewHandle } from "../BaseViewHandle";
import ColumnItemEndAddon from "../ColumnItemEndAddon";
import { ColumnTypeIcon } from "../ColumnTypeIcon";
import ColumnDetails from "./ColumnDetails";
import ColumnsPopoverContent from "./ColumnsPopoverContent";
import { useRenameColumnState } from "./useRenameColumnState";

const ColumnsPopover: FC<{ viewHandle: BaseViewHandle }> = ({ viewHandle }) => {
	const [popoverOpen, setPopoverOpen] = useState<"columns" | number | undefined>(undefined);
	const toggleEditColumnPopover = useCallback((columnIndex: number) => {
		setPopoverOpen((popoverOpen) =>
			typeof popoverOpen === "number" ? undefined : columnIndex
		);
	}, []);

	const column =
		typeof popoverOpen === "number" ? viewHandle.columnDefs.allColumns[popoverOpen] : undefined;

	const {
		currentHeaderValue,
		internalHeaderValue,
		setInternalHeaderValue,
		headerChanged,
		newAccessorKey,
	} = useRenameColumnState(viewHandle, column);

	return (
		<Popover
			open={popoverOpen !== undefined}
			onOpenChange={(open) => setPopoverOpen(open ? "columns" : undefined)}
		>
			<PopoverTrigger asChild>
				<Button preset="quaternary" size="md" className="!px-1.5">
					Columns
				</Button>
			</PopoverTrigger>
			<PopoverContent className="!w-96">
				{popoverOpen === "columns" && (
					<ColumnsPopoverContent
						viewHandle={viewHandle}
						toggleEditColumnPopover={toggleEditColumnPopover}
					/>
				)}
				{column && (
					<div className="flex flex-col gap-2">
						<div>
							<Input
								// `PopoverContent` is focus-trapped so this is ok.
								//
								// eslint-disable-next-line jsx-a11y/no-autofocus
								autoFocus
								value={internalHeaderValue}
								elevation="raised"
								endAddon={<ColumnItemEndAddon column={column} />}
								onChange={(e) => setInternalHeaderValue(e.target.value)}
								onBlur={() => {
									if (newAccessorKey) {
										const updatedColumn: ViewColumnDef = {
											...column,
											header: internalHeaderValue,
											accessorKey: `$.${newAccessorKey}` as const,
										};
										viewHandle.updateColumn(column.accessorKey, updatedColumn);
									}
								}}
							/>
							{headerChanged && (
								<div className="text-sm text-secondary my-1">
									⇢ and migrate data from{" "}
									<code className="font-medium">.{currentHeaderValue}</code> to{" "}
									<code className="font-medium">.{newAccessorKey}</code>
								</div>
							)}
						</div>
						<Select
							value={column.type}
							onValueChange={(value: ViewColumnDef["type"]) => {
								switch (value) {
									case "Text":
										viewHandle.updateColumn(column.accessorKey, {
											...column,
											type: "Text",
										});
										break;
									case "Select":
										viewHandle.updateColumn(column.accessorKey, {
											...column,
											type: "Select",
											typeInfo: { options: [] },
										});
										break;
									case "Reaction":
										viewHandle.updateColumn(column.accessorKey, {
											...column,
											type: "Reaction",
										});
										break;
									case "Person":
										viewHandle.updateColumn(column.accessorKey, {
											...column,
											type: "Person",
											typeInfo: { defaultValue: "Empty" },
										});
										break;
									default: {
										const _: never = value;
										throw new Error(`Unknown column type: ${_}`);
									}
								}
							}}
						>
							<SelectTrigger elevation="raised">
								<ColumnTypeIcon type={column.type} />
								<SelectValue placeholder="Select a type" className="flex gap-2">
									{column.type}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Text">Text</SelectItem>
								<SelectItem value="Reaction">Reaction</SelectItem>
								<SelectItem value="Select">Select List</SelectItem>
								<SelectItem value="Person">Person</SelectItem>
							</SelectContent>
						</Select>
						<ColumnDetails column={column} viewHandle={viewHandle} />
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
};

export default ColumnsPopover;
